import React, { useState, useEffect, useCallback } from 'react';
import { assetsApi, entitiesApi } from '../api/client';
import { Plus, Pencil, Trash2, X, Gem, AlertCircle, Search, ChevronRight } from 'lucide-react';

const ASSET_TYPES = [
  { value: 'real_estate', label: 'Real Estate' },
  { value: 'public_equity', label: 'Public Equity' },
  { value: 'private_equity', label: 'Private Equity' },
  { value: 'fixed_income', label: 'Fixed Income' },
  { value: 'cash', label: 'Cash' },
  { value: 'hedge_fund', label: 'Hedge Fund' },
  { value: 'venture_capital', label: 'Venture Capital' },
  { value: 'real_assets', label: 'Real Assets' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'retirement', label: 'Retirement' },
  { value: 'business_interest', label: 'Business Interest' },
  { value: 'other', label: 'Other' },
];

const PROPERTY_TYPES = [
  { value: 'office', label: 'Office' },
  { value: 'multifamily', label: 'Multifamily' },
  { value: 'retail', label: 'Retail' },
  { value: 'industrial', label: 'Industrial' },
  { value: 'land', label: 'Land' },
  { value: 'mixed_use', label: 'Mixed Use' },
  { value: 'hospitality', label: 'Hospitality' },
  { value: 'other', label: 'Other' },
];

const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','DC','FL','GA','HI','ID','IL','IN',
  'IA','KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH',
  'NJ','NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT',
  'VT','VA','WA','WV','WI','WY',
];

function typeLabel(val: string): string {
  return ASSET_TYPES.find(t => t.value === val)?.label || val;
}

function fmtCurrency(val: number | string | null | undefined): string {
  if (val == null) return '—';
  const n = typeof val === 'string' ? parseFloat(val) : val;
  if (isNaN(n)) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency', currency: 'USD',
    minimumFractionDigits: 0, maximumFractionDigits: 0,
  }).format(n);
}

function fmtCompact(val: number | string | null | undefined): string {
  if (val == null) return '$0';
  const n = typeof val === 'string' ? parseFloat(val) : val;
  if (isNaN(n)) return '$0';
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (Math.abs(n) >= 1_000) return `$${(n / 1_000).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

function fmtPct(val: number | string | null | undefined): string {
  if (val == null) return '—';
  const n = typeof val === 'string' ? parseFloat(val) : val;
  if (isNaN(n)) return '—';
  return `${(n * 100).toFixed(1)}%`;
}

interface Asset {
  id: string; name: string; asset_type: string; entity_id?: string; entity_name?: string;
  current_value: number | string; cost_basis?: number | string;
  appreciation_rate?: number | string; income_yield?: number | string;
  is_liquid?: boolean; liquidity_discount_pct?: number | string;
  step_up_eligible?: boolean; acquisition_date?: string; notes?: string;
  property_type?: string; property_address?: string; property_state?: string;
  noi?: number | string; cap_rate?: number | string;
  square_footage?: number; unit_count?: number;
  target_return_multiple?: number | string; investment_horizon_years?: number;
  discount_type?: string; discount_percentage?: number | string; estate_inclusion_value?: number | string;
  passes_by_beneficiary_designation?: boolean; asset_tax_character?: string;
}

interface EntityOption { id: string; name: string; }

interface FormData {
  name: string; assetType: string; entityId: string;
  currentValue: string; costBasis: string; appreciationRate: string;
  incomeYield: string; isLiquid: boolean; liquidityDiscountPct: string;
  stepUpEligible: boolean; acquisitionDate: string; notes: string;
  // RE fields
  propertyType: string; propertyAddress: string; propertyState: string;
  noi: string; capRate: string; squareFootage: string; unitCount: string;
  // Return multiple
  targetReturnMultiple: string; investmentHorizonYears: string;
  // Valuation discounts
  discountType: string; discountPercentage: string;
  // Beneficiary designation & tax character
  passesByBeneficiaryDesignation: boolean; assetTaxCharacter: string;
}

const emptyForm: FormData = {
  name: '', assetType: 'real_estate', entityId: '',
  currentValue: '', costBasis: '', appreciationRate: '5',
  incomeYield: '0', isLiquid: false, liquidityDiscountPct: '0',
  stepUpEligible: true, acquisitionDate: '', notes: '',
  propertyType: '', propertyAddress: '', propertyState: '',
  noi: '', capRate: '', squareFootage: '', unitCount: '',
  targetReturnMultiple: '', investmentHorizonYears: '',
  discountType: 'none', discountPercentage: '0',
  passesByBeneficiaryDesignation: false, assetTaxCharacter: 'not_applicable',
};

function TypeBadge({ type }: { type: string }) {
  const realAssets = ['real_estate', 'real_assets'];
  const equity = ['public_equity', 'private_equity', 'venture_capital', 'business_interest'];
  const fixed = ['fixed_income', 'cash'];
  let classes = 'badge-navy';
  if (realAssets.includes(type)) classes = 'badge-gold';
  else if (equity.includes(type)) classes = 'badge-burgundy';
  else if (fixed.includes(type)) classes = 'badge-hunter';
  return <span className={classes}>{typeLabel(type)}</span>;
}

export default function AssetsPage() {
  const [assets, setAssets] = useState<Asset[]>([]);
  const [entities, setEntities] = useState<EntityOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterEntity, setFilterEntity] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormData>(emptyForm);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  const fetchAssets = useCallback(async () => {
    try {
      setLoading(true);
      const params: any = { limit: 100 };
      if (filterType) params.type = filterType;
      const res = await assetsApi.list(params);
      setAssets(res.data.data || []);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load assets');
    } finally { setLoading(false); }
  }, [filterType]);

  const fetchEntities = useCallback(async () => {
    try {
      const res = await entitiesApi.list({ limit: 100 });
      setEntities((res.data.data || []).map((e: any) => ({ id: e.id, name: e.name })));
    } catch (_) {}
  }, []);

  useEffect(() => { fetchAssets(); fetchEntities(); }, [fetchAssets, fetchEntities]);

  const totalValue = assets.reduce((sum, a) => sum + (parseFloat(a.current_value as string) || 0), 0);
  const totalBasis = assets.reduce((sum, a) => sum + (parseFloat(a.cost_basis as string) || 0), 0);
  const liquidValue = assets.filter(a => a.is_liquid).reduce((sum, a) => sum + (parseFloat(a.current_value as string) || 0), 0);
  const filtered = assets
    .filter(a => !filterEntity || a.entity_id === filterEntity)
    .filter(a => !search || a.name.toLowerCase().includes(search.toLowerCase()));

  const isRealEstate = form.assetType === 'real_estate';
  const isAlternative = ['private_equity', 'venture_capital', 'hedge_fund'].includes(form.assetType);

  function openCreate() { setEditingId(null); setForm(emptyForm); setFormError(''); setShowModal(true); }

  function openEdit(asset: Asset) {
    setEditingId(asset.id);
    setForm({
      name: asset.name, assetType: asset.asset_type, entityId: asset.entity_id || '',
      currentValue: String(asset.current_value || ''),
      costBasis: String(asset.cost_basis || ''),
      appreciationRate: String((parseFloat(asset.appreciation_rate as string) || 0) * 100),
      incomeYield: String((parseFloat(asset.income_yield as string) || 0) * 100),
      isLiquid: asset.is_liquid || false,
      liquidityDiscountPct: String(asset.liquidity_discount_pct || '0'),
      stepUpEligible: asset.step_up_eligible !== false,
      acquisitionDate: asset.acquisition_date || '', notes: asset.notes || '',
      propertyType: asset.property_type || '', propertyAddress: asset.property_address || '',
      propertyState: asset.property_state || '', noi: String(asset.noi || ''),
      capRate: String((parseFloat(asset.cap_rate as string) || 0) * 100),
      squareFootage: String(asset.square_footage || ''),
      unitCount: String(asset.unit_count || ''),
      targetReturnMultiple: String(asset.target_return_multiple || ''),
      investmentHorizonYears: String(asset.investment_horizon_years || ''),
      discountType: asset.discount_type || 'none',
      discountPercentage: String(asset.discount_percentage || '0'),
      passesByBeneficiaryDesignation: asset.passes_by_beneficiary_designation || false,
      assetTaxCharacter: asset.asset_tax_character || 'not_applicable',
    });
    setFormError(''); setShowModal(true);
  }

  async function handleSave() {
    if (!form.name.trim()) { setFormError('Asset name is required'); return; }
    if (!form.currentValue || parseFloat(form.currentValue) < 0) { setFormError('Current value is required'); return; }
    try {
      setSaving(true); setFormError('');
      const payload: any = {
        name: form.name.trim(), assetType: form.assetType,
        entityId: form.entityId || undefined,
        currentValue: parseFloat(form.currentValue),
        costBasis: form.costBasis ? parseFloat(form.costBasis) : undefined,
        appreciationRate: parseFloat(form.appreciationRate) / 100,
        incomeYield: parseFloat(form.incomeYield) / 100,
        isLiquid: form.isLiquid,
        liquidityDiscountPct: parseFloat(form.liquidityDiscountPct) || 0,
        stepUpEligible: form.stepUpEligible,
        acquisitionDate: form.acquisitionDate || undefined,
        notes: form.notes || undefined,
      };
      // RE fields
      if (isRealEstate) {
        payload.propertyType = form.propertyType || undefined;
        payload.propertyAddress = form.propertyAddress || undefined;
        payload.propertyState = form.propertyState || undefined;
        payload.noi = form.noi ? parseFloat(form.noi) : undefined;
        payload.capRate = form.capRate ? parseFloat(form.capRate) / 100 : undefined;
        payload.squareFootage = form.squareFootage ? parseInt(form.squareFootage) : undefined;
        payload.unitCount = form.unitCount ? parseInt(form.unitCount) : undefined;
      }
      // Return multiple
      if (isAlternative || form.targetReturnMultiple) {
        payload.targetReturnMultiple = form.targetReturnMultiple ? parseFloat(form.targetReturnMultiple) : undefined;
        payload.investmentHorizonYears = form.investmentHorizonYears ? parseInt(form.investmentHorizonYears) : undefined;
      }
      payload.discountType = form.discountType || 'none';
      payload.discountPercentage = parseFloat(form.discountPercentage) || 0;
      payload.passesByBeneficiaryDesignation = form.passesByBeneficiaryDesignation;
      payload.assetTaxCharacter = form.assetTaxCharacter;
      if (editingId) await assetsApi.update(editingId, payload);
      else await assetsApi.create(payload);
      setShowModal(false); fetchAssets();
    } catch (err: any) {
      setFormError(err.response?.data?.error || 'Failed to save asset');
    } finally { setSaving(false); }
  }

  async function handleDelete() {
    if (!deleteId) return;
    try { setDeleting(true); await assetsApi.delete(deleteId); setDeleteId(null); fetchAssets(); }
    catch (err: any) { setError(err.response?.data?.error || 'Failed to delete'); setDeleteId(null); }
    finally { setDeleting(false); }
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-2xl text-navy-900">Asset Register</h1>
          <p className="text-sm text-navy-400 font-sans mt-1">Portfolio holdings across all entities and asset classes</p>
        </div>
        <button onClick={openCreate} className="btn-primary"><Plus size={16} className="mr-2" />Add Asset</button>
      </div>

      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="card"><p className="stat-label mb-1">Total Assets</p><p className="font-display text-xl text-navy-900">{fmtCompact(totalValue)}</p><p className="text-xs text-navy-400 font-sans mt-1">{assets.length} holdings</p></div>
        <div className="card"><p className="stat-label mb-1">Total Basis</p><p className="font-display text-xl text-navy-900">{fmtCompact(totalBasis)}</p><p className="text-xs text-navy-400 font-sans mt-1">{totalValue > totalBasis ? `+${fmtCompact(totalValue - totalBasis)} gain` : '—'}</p></div>
        <div className="card"><p className="stat-label mb-1">Liquid Assets</p><p className="font-display text-xl text-hunter-500">{fmtCompact(liquidValue)}</p><p className="text-xs text-navy-400 font-sans mt-1">{totalValue > 0 ? `${((liquidValue / totalValue) * 100).toFixed(0)}% of portfolio` : '—'}</p></div>
        <div className="card"><p className="stat-label mb-1">Asset Classes</p><p className="font-display text-xl text-navy-900">{new Set(assets.map(a => a.asset_type)).size}</p><p className="text-xs text-navy-400 font-sans mt-1">unique types</p></div>
      </div>

      <div className="card mb-6">
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-xs">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-navy-300" />
            <input type="text" placeholder="Search assets..." value={search} onChange={(e) => setSearch(e.target.value)} className="input pl-9" />
          </div>
          <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="input w-auto">
            <option value="">All Types</option>
            {ASSET_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <select value={filterEntity} onChange={(e) => setFilterEntity(e.target.value)} className="input w-auto">
            <option value="">All Entities</option>
            {entities.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
          {(filterEntity || filterType) && (
            <button onClick={() => { setFilterEntity(''); setFilterType(''); }} className="text-xs text-navy-400 hover:text-burgundy-500 font-sans underline">Clear filters</button>
          )}
          <div className="ml-auto text-sm text-navy-400 font-sans">{filtered.length} {filtered.length === 1 ? 'asset' : 'assets'}</div>
        </div>
      </div>

      {error && <div className="flex items-center gap-2 p-4 mb-4 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm"><AlertCircle size={16} />{error}</div>}

      <div className="card p-0 overflow-hidden">
        {loading ? (
          <div className="p-12 text-center"><p className="text-sm text-navy-400 font-sans animate-pulse">Loading assets...</p></div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center"><Gem size={32} className="mx-auto mb-3 text-navy-200" /><p className="text-sm text-navy-500 font-sans font-medium">No assets yet</p><p className="text-xs text-navy-400 font-sans mt-1">Add your first asset or import from Excel</p></div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-ivory-300 bg-ivory-200">
                  <th className="table-header w-10 px-2 py-3"></th>
                  <th className="table-header text-left px-6 py-3">Name</th>
                  <th className="table-header text-left px-4 py-3">Type</th>
                  <th className="table-header text-left px-4 py-3">Entity</th>
                  <th className="table-header text-right px-4 py-3">FMV</th>
                  <th className="table-header text-right px-4 py-3">Basis</th>
                  <th className="table-header text-right px-4 py-3">Yield</th>
                  <th className="table-header text-center px-4 py-3">Liquid</th>
                  <th className="table-header text-right px-6 py-3 w-24">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ivory-200">
                {filtered.map((asset) => (
                  <React.Fragment key={asset.id}>
                  <tr className="hover:bg-ivory-100 transition-colors">
                    <td className="px-2 py-3.5 text-center">
                      <button onClick={() => setExpandedId(expandedId === asset.id ? null : asset.id)} className="p-1 rounded hover:bg-ivory-200 text-navy-400 hover:text-navy-700 transition-all">
                        <ChevronRight size={14} className={`transition-transform duration-200 ${expandedId === asset.id ? 'rotate-90' : ''}`} />
                      </button>
                    </td>
                    <td className="px-6 py-3.5">
                      <span className="font-sans text-sm font-semibold text-navy-800">{asset.name}</span>
                      {asset.property_state && <span className="ml-2 text-xs text-navy-400">({asset.property_state})</span>}
                      {asset.target_return_multiple && <span className="ml-2 badge-burgundy">{asset.target_return_multiple}×</span>}
                    </td>
                    <td className="px-4 py-3.5"><TypeBadge type={asset.asset_type} /></td>
                    <td className="px-4 py-3.5 text-sm text-navy-500 font-sans">{asset.entity_name || '—'}</td>
                    <td className="px-4 py-3.5 text-right font-mono text-sm text-navy-700 font-medium">{fmtCurrency(asset.current_value)}</td>
                    <td className="px-4 py-3.5 text-right font-mono text-sm text-navy-500">{fmtCurrency(asset.cost_basis)}</td>
                    <td className="px-4 py-3.5 text-right font-mono text-sm text-navy-500">{fmtPct(asset.income_yield)}</td>
                    <td className="px-4 py-3.5 text-center">
                      {asset.is_liquid ? <span className="badge-hunter">Yes</span> : <span className="text-xs text-navy-300">No</span>}
                    </td>
                    <td className="px-6 py-3.5 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => openEdit(asset)} className="p-1.5 rounded hover:bg-ivory-200 text-navy-400 hover:text-navy-700 transition-colors" title="Edit"><Pencil size={14} /></button>
                        <button onClick={() => setDeleteId(asset.id)} className="p-1.5 rounded hover:bg-red-50 text-navy-400 hover:text-red-600 transition-colors" title="Delete"><Trash2 size={14} /></button>
                      </div>
                    </td>
                  </tr>
                  {expandedId === asset.id && (
                    <tr className="bg-ivory-100 border-b border-ivory-200">
                      <td colSpan={9} className="px-6 py-4">
                        <div className="grid grid-cols-3 gap-6">
                          <div>
                            <p className="text-xs font-sans font-semibold text-navy-500 uppercase tracking-wider mb-2">Valuation</p>
                            <div className="space-y-1.5">
                              <div className="flex justify-between"><span className="text-xs text-navy-400 font-sans">Current FMV</span><span className="text-xs text-navy-700 font-mono font-medium">{fmtCurrency(asset.current_value)}</span></div>
                              <div className="flex justify-between"><span className="text-xs text-navy-400 font-sans">Cost Basis</span><span className="text-xs text-navy-700 font-mono">{fmtCurrency(asset.cost_basis)}</span></div>
                              {parseFloat(asset.current_value as string) > 0 && parseFloat(asset.cost_basis as string) > 0 && (
                                <div className="flex justify-between"><span className="text-xs text-navy-400 font-sans">Unrealized Gain</span><span className={`text-xs font-mono font-medium ${(parseFloat(asset.current_value as string) - parseFloat(asset.cost_basis as string)) >= 0 ? 'text-hunter-500' : 'text-burgundy-500'}`}>{fmtCurrency(parseFloat(asset.current_value as string) - parseFloat(asset.cost_basis as string))}</span></div>
                              )}
                              <div className="flex justify-between"><span className="text-xs text-navy-400 font-sans">Appreciation</span><span className="text-xs text-navy-700 font-mono">{fmtPct(asset.appreciation_rate)}</span></div>
                              <div className="flex justify-between"><span className="text-xs text-navy-400 font-sans">Income Yield</span><span className="text-xs text-navy-700 font-mono">{fmtPct(asset.income_yield)}</span></div>
                            </div>
                          </div>
                          <div>
                            <p className="text-xs font-sans font-semibold text-navy-500 uppercase tracking-wider mb-2">Classification</p>
                            <div className="space-y-1.5">
                              <div className="flex justify-between"><span className="text-xs text-navy-400 font-sans">Entity</span><span className="text-xs text-navy-700 font-sans">{asset.entity_name || 'Personal'}</span></div>
                              <div className="flex justify-between"><span className="text-xs text-navy-400 font-sans">Liquid</span><span className={`text-xs font-sans font-medium ${asset.is_liquid ? 'text-hunter-500' : 'text-navy-400'}`}>{asset.is_liquid ? 'Yes' : 'No'}</span></div>
                              <div className="flex justify-between"><span className="text-xs text-navy-400 font-sans">Step-Up</span><span className="text-xs text-navy-700 font-sans">{asset.step_up_eligible !== false ? 'Eligible' : 'No'}</span></div>
                              {asset.acquisition_date && <div className="flex justify-between"><span className="text-xs text-navy-400 font-sans">Acquired</span><span className="text-xs text-navy-700 font-sans">{new Date(asset.acquisition_date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</span></div>}
                              {asset.discount_type && asset.discount_type !== 'none' && (
                                <div className="flex justify-between"><span className="text-xs text-navy-400 font-sans">Discount</span><span className="text-xs text-navy-700 font-sans">{asset.discount_type.toUpperCase()} {asset.discount_percentage}%</span></div>
                              )}
                            </div>
                          </div>
                          <div>
                            {asset.asset_type === 'real_estate' ? (
                              <>
                                <p className="text-xs font-sans font-semibold text-navy-500 uppercase tracking-wider mb-2">Real Estate</p>
                                <div className="space-y-1.5">
                                  {asset.property_type && <div className="flex justify-between"><span className="text-xs text-navy-400 font-sans">Property Type</span><span className="text-xs text-navy-700 font-sans capitalize">{asset.property_type}</span></div>}
                                  {asset.property_state && <div className="flex justify-between"><span className="text-xs text-navy-400 font-sans">Situs</span><span className="text-xs text-navy-700 font-sans">{asset.property_state}</span></div>}
                                  {asset.noi && <div className="flex justify-between"><span className="text-xs text-navy-400 font-sans">NOI</span><span className="text-xs text-navy-700 font-mono">{fmtCurrency(asset.noi)}</span></div>}
                                  {asset.cap_rate && <div className="flex justify-between"><span className="text-xs text-navy-400 font-sans">Cap Rate</span><span className="text-xs text-navy-700 font-mono">{fmtPct(asset.cap_rate)}</span></div>}
                                  {asset.square_footage && <div className="flex justify-between"><span className="text-xs text-navy-400 font-sans">Sq Ft</span><span className="text-xs text-navy-700 font-mono">{asset.square_footage.toLocaleString()}</span></div>}
                                  {asset.unit_count && <div className="flex justify-between"><span className="text-xs text-navy-400 font-sans">Units</span><span className="text-xs text-navy-700 font-mono">{asset.unit_count}</span></div>}
                                </div>
                              </>
                            ) : (
                              <>
                                <p className="text-xs font-sans font-semibold text-navy-500 uppercase tracking-wider mb-2">Additional</p>
                                <div className="space-y-1.5">
                                  {asset.target_return_multiple && <div className="flex justify-between"><span className="text-xs text-navy-400 font-sans">Target Multiple</span><span className="text-xs text-navy-700 font-mono">{asset.target_return_multiple}×</span></div>}
                                  {asset.investment_horizon_years && <div className="flex justify-between"><span className="text-xs text-navy-400 font-sans">Horizon</span><span className="text-xs text-navy-700 font-sans">{asset.investment_horizon_years} years</span></div>}
                                  {asset.asset_tax_character && asset.asset_tax_character !== 'not_applicable' && <div className="flex justify-between"><span className="text-xs text-navy-400 font-sans">Tax Character</span><span className="text-xs text-navy-700 font-sans capitalize">{asset.asset_tax_character.replace(/_/g, ' ')}</span></div>}
                                  {asset.passes_by_beneficiary_designation && <div className="flex justify-between"><span className="text-xs text-navy-400 font-sans">Beneficiary</span><span className="text-xs text-hunter-500 font-sans font-medium">By designation</span></div>}
                                  {asset.estate_inclusion_value && <div className="flex justify-between"><span className="text-xs text-navy-400 font-sans">Estate Inclusion</span><span className="text-xs text-navy-700 font-mono">{fmtCurrency(asset.estate_inclusion_value)}</span></div>}
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                        {asset.notes && (
                          <div className="mt-3 pt-3 border-t border-ivory-300">
                            <p className="text-xs text-navy-400 font-sans italic">{asset.notes}</p>
                          </div>
                        )}
                        <div className="mt-3 flex justify-end">
                          <button onClick={() => openEdit(asset)} className="text-xs font-sans font-medium text-navy-500 hover:text-navy-700 underline">Edit full details</button>
                        </div>
                      </td>
                    </tr>
                  )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Create/Edit Modal ──────────────────────────── */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
          <div className="bg-white rounded-lg shadow-xl border border-ivory-300 w-full max-w-xl mx-4">
            <div className="flex items-center justify-between px-6 py-4 border-b border-ivory-300">
              <h3 className="font-display text-lg text-navy-900">{editingId ? 'Edit Asset' : 'Add Asset'}</h3>
              <button onClick={() => setShowModal(false)} className="p-1.5 rounded hover:bg-ivory-200 text-navy-400"><X size={18} /></button>
            </div>
            <div className="px-6 py-5 space-y-4 max-h-[65vh] overflow-y-auto">
              {formError && <div className="flex items-center gap-2 p-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm"><AlertCircle size={14} />{formError}</div>}

              <div>
                <label className="input-label">Asset Name *</label>
                <input type="text" className="input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g., Office Complex — Hartford" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="input-label">Asset Type *</label>
                  <select className="input" value={form.assetType} onChange={(e) => setForm({ ...form, assetType: e.target.value })}>
                    {ASSET_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className="input-label">Held In Entity</label>
                  <select className="input" value={form.entityId} onChange={(e) => setForm({ ...form, entityId: e.target.value })}>
                    <option value="">— Direct / Personal —</option>
                    {entities.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="input-label">Current FMV ($) *</label>
                  <input type="number" className="input" value={form.currentValue} onChange={(e) => setForm({ ...form, currentValue: e.target.value })} placeholder="0" min="0" />
                </div>
                <div>
                  <label className="input-label">Cost Basis ($)</label>
                  <input type="number" className="input" value={form.costBasis} onChange={(e) => setForm({ ...form, costBasis: e.target.value })} placeholder="0" min="0" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="input-label">Appreciation Rate (%)</label>
                  <input type="number" className="input" value={form.appreciationRate} onChange={(e) => setForm({ ...form, appreciationRate: e.target.value })} placeholder="5" step="0.1" />
                </div>
                <div>
                  <label className="input-label">Income Yield (%)</label>
                  <input type="number" className="input" value={form.incomeYield} onChange={(e) => setForm({ ...form, incomeYield: e.target.value })} placeholder="0" step="0.1" />
                </div>
              </div>

              <div>
                <label className="input-label">Acquisition Date</label>
                <input type="date" className="input" value={form.acquisitionDate} onChange={(e) => setForm({ ...form, acquisitionDate: e.target.value })} />
              </div>

              <div className="flex items-center gap-6 py-1">
                <label className="flex items-center gap-2 text-sm text-navy-700 font-sans">
                  <input type="checkbox" checked={form.isLiquid} onChange={(e) => setForm({ ...form, isLiquid: e.target.checked })} className="w-4 h-4 rounded border-ivory-400 text-navy-600 focus:ring-navy-300" />
                  Liquid asset
                </label>
                <label className="flex items-center gap-2 text-sm text-navy-700 font-sans">
                  <input type="checkbox" checked={form.stepUpEligible} onChange={(e) => setForm({ ...form, stepUpEligible: e.target.checked })} className="w-4 h-4 rounded border-ivory-400 text-navy-600 focus:ring-navy-300" />
                  Step-up eligible
                </label>
              </div>

              {/* ── Real Estate Section ─────────────────── */}
              {isRealEstate && (
                <div className="border-t border-ivory-300 pt-4 mt-2">
                  <p className="text-xs font-sans font-semibold text-navy-500 uppercase tracking-wider mb-3">Real Estate Detail</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="input-label">Property Type</label>
                      <select className="input" value={form.propertyType} onChange={(e) => setForm({ ...form, propertyType: e.target.value })}>
                        <option value="">Select type</option>
                        {PROPERTY_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="input-label">Situs State</label>
                      <select className="input" value={form.propertyState} onChange={(e) => setForm({ ...form, propertyState: e.target.value })}>
                        <option value="">Select state</option>
                        {US_STATES.map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  </div>
                  <div className="mt-4">
                    <label className="input-label">Property Address</label>
                    <input type="text" className="input" value={form.propertyAddress} onChange={(e) => setForm({ ...form, propertyAddress: e.target.value })} placeholder="123 Main St, City, State" />
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="input-label">Net Operating Income ($)</label>
                      <input type="number" className="input" value={form.noi} onChange={(e) => setForm({ ...form, noi: e.target.value })} placeholder="0" min="0" />
                    </div>
                    <div>
                      <label className="input-label">Cap Rate (%)</label>
                      <input type="number" className="input" value={form.capRate} onChange={(e) => setForm({ ...form, capRate: e.target.value })} placeholder="6.0" step="0.1" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div>
                      <label className="input-label">Square Footage</label>
                      <input type="number" className="input" value={form.squareFootage} onChange={(e) => setForm({ ...form, squareFootage: e.target.value })} placeholder="0" min="0" />
                    </div>
                    <div>
                      <label className="input-label">Unit Count</label>
                      <input type="number" className="input" value={form.unitCount} onChange={(e) => setForm({ ...form, unitCount: e.target.value })} placeholder="0" min="0" />
                    </div>
                  </div>
                </div>
              )}

              {/* ── Return Multiple Section ─────────────── */}
              {(isAlternative || form.targetReturnMultiple) && (
                <div className="border-t border-ivory-300 pt-4 mt-2">
                  <p className="text-xs font-sans font-semibold text-navy-500 uppercase tracking-wider mb-3">Return Multiple</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="input-label">Target Multiple (×)</label>
                      <input type="number" className="input" value={form.targetReturnMultiple} onChange={(e) => setForm({ ...form, targetReturnMultiple: e.target.value })} placeholder="2.5" step="0.1" min="0" />
                    </div>
                    <div>
                      <label className="input-label">Investment Horizon (years)</label>
                      <input type="number" className="input" value={form.investmentHorizonYears} onChange={(e) => setForm({ ...form, investmentHorizonYears: e.target.value })} placeholder="7" min="1" max="50" />
                    </div>
                  </div>
                  {form.targetReturnMultiple && form.investmentHorizonYears && parseFloat(form.targetReturnMultiple) > 0 && parseInt(form.investmentHorizonYears) > 0 && (
                    <p className="text-xs text-navy-500 font-sans mt-2 italic">
                      Implied IRR: {((Math.pow(parseFloat(form.targetReturnMultiple), 1 / parseInt(form.investmentHorizonYears)) - 1) * 100).toFixed(1)}% annualized
                    </p>
                  )}
                </div>
              )}

              {/* ── Beneficiary Designation & Tax Character ── */}
              {['retirement', 'insurance'].includes(form.assetType) && (
                <div className="border-t border-ivory-300 pt-4 mt-2">
                  <p className="text-xs font-sans font-semibold text-navy-500 uppercase tracking-wider mb-3">Beneficiary Designation & Tax Character</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="input-label">Tax Character</label>
                      <select className="input" value={form.assetTaxCharacter} onChange={(e) => setForm({ ...form, assetTaxCharacter: e.target.value })}>
                        <option value="not_applicable">Not Applicable</option>
                        <option value="pre_tax_qualified">Pre-Tax Qualified (401k, Trad. IRA)</option>
                        <option value="roth">Roth (Tax-Free Growth)</option>
                        <option value="after_tax">After-Tax (Non-Qualified Annuity)</option>
                      </select>
                    </div>
                    <div className="flex items-end pb-1">
                      <label className="flex items-center gap-2 text-sm text-navy-700 font-sans">
                        <input type="checkbox" checked={form.passesByBeneficiaryDesignation} onChange={(e) => setForm({ ...form, passesByBeneficiaryDesignation: e.target.checked })} className="w-4 h-4 rounded border-ivory-400 text-navy-600 focus:ring-navy-300" />
                        Passes by beneficiary designation
                      </label>
                    </div>
                  </div>
                  {form.assetTaxCharacter === 'pre_tax_qualified' && form.currentValue && (
                    <div className="mt-3 p-3 rounded bg-amber-50 border border-amber-200">
                      <p className="text-xs font-sans font-semibold text-amber-800 mb-1">IRD Warning — Double Taxation Risk</p>
                      <p className="text-xs font-sans text-amber-700">
                        Pre-tax qualified assets are included in the gross estate AND subject to income tax when distributed to beneficiaries.
                        At a 40% estate rate + 37% income rate, the effective combined rate can reach ~62% of the account value.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* ── Valuation Discount Section ─────────── */}
              {['business_interest', 'private_equity', 'venture_capital', 'real_estate', 'real_assets', 'other'].includes(form.assetType) && (
                <div className="border-t border-ivory-300 pt-4 mt-2">
                  <p className="text-xs font-sans font-semibold text-navy-500 uppercase tracking-wider mb-3">Valuation Discount</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="input-label">Discount Type</label>
                      <select className="input" value={form.discountType} onChange={(e) => setForm({ ...form, discountType: e.target.value })}>
                        <option value="none">None</option>
                        <option value="dlom">DLOM (Lack of Marketability)</option>
                        <option value="dloc">DLOC (Lack of Control)</option>
                        <option value="combined">Combined DLOM + DLOC</option>
                      </select>
                    </div>
                    <div>
                      <label className="input-label">Discount (%)</label>
                      <input type="number" className="input" value={form.discountPercentage} onChange={(e) => setForm({ ...form, discountPercentage: e.target.value })} placeholder="0" step="1" min="0" max="100" disabled={form.discountType === 'none'} />
                    </div>
                  </div>
                  {form.discountType !== 'none' && parseFloat(form.discountPercentage) > 0 && form.currentValue && (
                    <p className="text-xs text-navy-500 font-sans mt-2 italic">
                      Estate inclusion value: {fmtCurrency(parseFloat(form.currentValue) * (1 - parseFloat(form.discountPercentage) / 100))}
                      {' '}(FMV {fmtCurrency(parseFloat(form.currentValue))} less {form.discountPercentage}% discount)
                    </p>
                  )}
                </div>
              )}
              <div>
                <label className="input-label">Notes</label>
                <textarea className="input" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Optional notes..." />
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-ivory-300 bg-ivory-100 rounded-b-lg">
              <button onClick={() => setShowModal(false)} className="btn-secondary" disabled={saving}>Cancel</button>
              <button onClick={handleSave} className="btn-primary" disabled={saving}>
                {saving ? 'Saving...' : editingId ? 'Update Asset' : 'Create Asset'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm">
          <div className="bg-white rounded-lg shadow-xl border border-ivory-300 w-full max-w-sm mx-4 p-6">
            <h3 className="font-display text-lg text-navy-900 mb-2">Delete Asset</h3>
            <p className="text-sm text-navy-500 font-sans mb-6">Are you sure you want to remove this asset? This cannot be undone.</p>
            <div className="flex items-center justify-end gap-3">
              <button onClick={() => setDeleteId(null)} className="btn-secondary" disabled={deleting}>Cancel</button>
              <button onClick={handleDelete} className="btn-burgundy" disabled={deleting}>{deleting ? 'Deleting...' : 'Delete'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
